import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { countries, states } from '../../Components/Enums/DataEnums';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from '../../Components/UI/dialog';
import { Button } from '../../Components/UI/button';
import { format } from 'date-fns';
import { checkIfInqOnlyMode, getUsersArr } from '../../Store/Reducers';
import { isLive } from '../../Utils/Utilities';
import { ArrowLeft } from 'lucide-react';
import useIsMobile from '../../Hooks/useIsMobile';
import { canShowImportInquiries } from '../../Utils/FeatureFlags';

// Utility to convert Excel serial or date to formatted string
const convertExcelDateToJSDate = (value) => {
  if (value instanceof Date) return format(value, 'yyyy-MM-dd');
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    const jsDate = new Date(Date.UTC(1899, 11, 30) + value * 86400 * 1000);
    return format(jsDate, 'yyyy-MM-dd');
  }
  return null;
};

// Get country & state objects from names
const getCountryState = (countryName, stateName) => {
  const countryObj = countries.find(c => c.Country.toLowerCase() === countryName.toLowerCase()) || { CountryID: '', CC: '' };
  let stateObj = null;

  if (stateName && countryObj.CountryID === 1) {
    stateObj = states.find(s => s.State.toLowerCase() === stateName.toLowerCase() && s.CountryID === 1) || null;
  }

  return { countryObj, stateObj };
};

// Tailwind class 
const classes = {
  container: 'px-4 pb-4 bg-gray-100 sahi-radius flex-1 mt-0',
  listItem: 'flex items-start gap-2',
  listNumber: 'font-semibold text-gray-800 min-w-[20px]',
  listText: 'text-gray-800',
  listHighlight: 'text-[--sahi-orange] font-medium',
  templateButton: 'text-[--sahi-dark-green] hover:text-[--sahi-primary-color] hover:text-opacity-80 underline font-medium flex items-center gap-2 mb-2 p-0',
  buttonContainer: 'flex gap-2 justify-start'
};

const ImportExcel = ({ open, onClose, triggerButton = true }) => {
  const navigate = useNavigate();
  const [excelData, setExcelData] = useState([]);
  const fileInputRef = useRef(null);
  const { data: connectionData, loading, error } = useSelector(state => state.connectionData);
  const users = useSelector(getUsersArr);
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
  const isMobile = useIsMobile();

  // Reset excelData whenever dialog opens
  useEffect(() => {
    if (open) setExcelData([]);
  }, [open]);
  const regions = useSelector((state) => state.masterData.regions);


  const downloadTemplate = () => {
    let actualFileName = regions.length > 0
      ? "SahiCRM Template for Contacts with Region.xlsx"
      : "SahiCRM Template for Contacts.xlsx";
    let downloadAsName = regions.length > 0
      ? "SahiClub Template for Contacts with Region.xlsx"
      : "SahiClub Template for Contacts.xlsx";
    const link = document.createElement('a');
    link.href = (isLive() ? "/Web" : "") + ("/Templates/" + actualFileName);
    link.download = downloadAsName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setExcelData([]);
      toast.error('No file selected. Please upload a valid Excel file.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

        if (jsonData.length < 2) {
          toast.error('Excel file must contain at least a header row and one data row.');
          return;
        }

        // Normalize headers: remove trailing " *" to match code keys (e.g. "Company *" -> "Company")
        const headers = jsonData[0].map(h => String(h).trim().replace(/\s+\*$/, ''));
        const expectedColumns = [
          'Company', 'Contact Name', 'Mobile', 'Email', 'Connection Type', 'Source', 'Country', 'State', 'City', 'GSTIN',
          'In-charge', 'Received on', 'Notes', 'Last Record on', 'Last Record Notes', 'Next Task on', 'Next Task Notes',
          'Tags', 'Potential', 'Category'
        ];

        const missingColumns = expectedColumns.filter(col => !headers.includes(col));
        if (missingColumns.length > 0) {
          setExcelData([]);
          toast.error(`Missing required columns: ${missingColumns.join(', ')}`);
          return;
        }

        const formattedData = jsonData.slice(1)
          .filter(row => row.some(cell => cell !== '' && cell != null))
          .map(row => {
            const rawMobile = String(row[headers.indexOf('Mobile')] || '').trim();
            let { countryObj, stateObj } = getCountryState(row[headers.indexOf('Country')], row[headers.indexOf('State')]);
            let finalMobile = rawMobile;

            // Handle numbers starting with '+' (e.g., +918908908903)
            if (rawMobile.startsWith('+')) {
              const numericPart = rawMobile.replace(/\D/g, '');
              // Sort countries by CC length descending to match longest code first
              const matchedCountry = [...countries]
                .sort((a, b) => String(b.CC).length - String(a.CC).length)
                .find(c => numericPart.startsWith(String(c.CC)));

              if (matchedCountry) {
                countryObj = matchedCountry;
                finalMobile = numericPart.substring(String(matchedCountry.CC).length);
                // Re-fetch state object based on the new matched country
                const stateVal = row[headers.indexOf('State')];
                if (stateVal && countryObj.CountryID === 1) {
                  stateObj = states.find(s => s.State.toLowerCase() === stateVal.toLowerCase() && s.CountryID === 1) || null;
                }
              }
            }

            // Look for "Connection Type" (asterisk was stripped in headers definition)
            const connTypeValue = String(row[headers.indexOf('Connection Type')] || '').trim();
            const types = connTypeValue.split(',').map(t => t.trim());
            const typeLetters = [];
            if (types.includes('Prospect') || types.includes('Future Prospect')) typeLetters.push('P');
            if (types.includes('Customer') || types.includes('Existing Customer')) typeLetters.push('C');
            if (types.includes('Supplier')) typeLetters.push('S');
            const connType = typeLetters.join('');

            return {
              ConnID: 0,
              BizName: row[headers.indexOf('Company')] || '',
              ContactName: row[headers.indexOf('Contact Name')] || '',
              Contacts: '[]',
              Mobile: finalMobile,
              CC: countryObj.CC || '',
              Email: row[headers.indexOf('Email')] || '',
              POCUserID: users.find(u => u.name?.toLowerCase() === String(row[headers.indexOf('In-charge')]).trim().toLowerCase())?.userID?.toString() || '',
              ReceivedOnDT: row[headers.indexOf('Received on')] ? convertExcelDateToJSDate(row[headers.indexOf('Received on')]) : null,
              Source: String(row[headers.indexOf('Source')] || '').trim(),
              Notes: row[headers.indexOf('Notes')] || '',
              City: row[headers.indexOf('City')] || '',
              StateID: Number(stateObj?.StateID) || 0,
              State: row[headers.indexOf('State')] || '',
              CountryID: Number(countryObj?.CountryID) || 0,
              Country: countryObj?.Country || '',
              GSTIN: row[headers.indexOf('GSTIN')] || '',
              LastRecord: row[headers.indexOf('Last Record Notes')] || '',
              LastRecordDT: row[headers.indexOf('Last Record on')] ? convertExcelDateToJSDate(row[headers.indexOf('Last Record on')]) : null,
              NextTask: row[headers.indexOf('Next Task Notes')] || null,
              NextTaskDT: row[headers.indexOf('Next Task on')] ? convertExcelDateToJSDate(row[headers.indexOf('Next Task on')]) : null,
              NextTaskTyp: row[headers.indexOf('Next Task Notes')] ? 'Follow-up' : '',
              Tags: row[headers.indexOf('Tags')] || null,
              Potential: row[headers.indexOf('Potential')] || '0',
              Segment: row[headers.indexOf('Category')] || '',
              RegionID: regions.find(rgn => rgn.Rgn.toLowerCase() === row[headers?.indexOf('Region')]?.toLowerCase())?.RID || 0,
              ConnType: connType,
              Fields: '[]',
              IsError: 0,
              ErrMsg: ''
            };
          });

        if (!formattedData.length) {
          toast.error('No valid data found in the Excel file.');
          return;
        }

        setExcelData(formattedData);
        navigate('/correct-excel', { state: { excelData: formattedData, connectionData, loading, error } });
        onClose();
      } catch (err) {
        setExcelData([]);
        toast.error(`Failed to process Excel file: ${err.message}`);
      }
    };

    reader.onerror = () => toast.error('Failed to read the file. Please try again.');
    reader.readAsArrayBuffer(file);
  };

  const triggerFileInput = () => fileInputRef.current?.click();

  if (!open || !onClose || !canShowImportInquiries()) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {triggerButton && <DialogTrigger asChild />}
      <DialogContent className="w-[560px] p-0">
        {isMobile ? (
          <DialogHeader className="dialog-header flex items-center justify-between px-4 pt-4">
            <DialogClose className=" p-1 sahi-radius">
              <ArrowLeft className="h-5 w-5" />
            </DialogClose>

            <div className="flex items-center gap-2">
              <DialogTitle className="font-semibold text-lg">
                Import from Excel
              </DialogTitle>
            </div>
          </DialogHeader>
        ) : (
          <DialogHeader className="dialog-header flex items-center justify-between px-4 pt-4">
            <div className="flex items-center gap-2">
              <DialogTitle className="font-semibold text-lg">
                Import from Excel
              </DialogTitle>
            </div>

            <DialogClose className="p-1 sahi-radius">
              <X className="h-5 w-5" />
            </DialogClose>
          </DialogHeader>
        )}

        <div className={classes.container}>
          <div className="mb-4">
            <h3 className="text-base font-semibold text-gray-800 mb-3">Have you ensured the following?</h3>
            <div className="space-y-2 text-sm">
              {[
                { number: 1, text: <>Column <span className={classes.listHighlight}>names</span> are the same as in templates.</> },
                { number: 2, text: <>Compulsory fields (<span className={classes.listHighlight}>Company, Contact Name, Mobile, Connection Type</span>) must be included.</> },
                { number: 3, text: <>Indian Mobile number must be <span className={classes.listHighlight}>10 digits</span> long (no 91 prefix).</> },
                { number: 4, text: <>No spaces in <span className={classes.listHighlight}>mobile</span> or <span className={classes.listHighlight}>email</span> fields.</> },
                { number: 5, text: <><span className={classes.listHighlight}>Country, State, In-charge</span> match exactly with the lists.</> }
              ].map(item => (
                <div key={item.number} className={classes.listItem}>
                  <span className={classes.listNumber}>{item.number}.</span>
                  <div className={classes.listText}>{item.text}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <Button variant="link" onClick={downloadTemplate} className={classes.templateButton}>
              <Download className="h-4 w-4 text-[--sahi-primary-color]" />
              Download Template
            </Button>
          </div>

          <div className={classes.buttonContainer}>
            <input
              type="file"
              accept=".xlsx, .xls"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button onClick={triggerFileInput}>Import</Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportExcel;
